// Тихая плашка с добрым словом: появляется после сделанной работы, гаснет сама,
// не перехватывает фокус и выключается одной кнопкой в шапке.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ActionIcon, Group, Paper, Text, Tooltip, Transition } from '@mantine/core';
import { qs } from '../api/client.ts';
import { useApiQuery, useWarehouse } from '../lib/hooks.ts';
import {
  IDLE_GAP_MS, SHOW_MS, type DayStats, type Phrase,
  eventPhrases, idlePhrases, isBusy, mayShow, moodStore, pickPhrase,
} from '../lib/encouragement.ts';

export function useMood() {
  return useSyncExternalStore(moodStore.subscribe, moodStore.get);
}

/** Выключатель в шапке — настройка личная, хранится в браузере */
export function MoodToggle() {
  const mood = useMood();
  return (
    <Tooltip label={mood.on ? 'Добрые слова включены' : 'Добрые слова выключены'}>
      <ActionIcon
        variant="subtle" size="lg" color={mood.on ? 'yellow' : 'gray'}
        aria-label="Подбадривающие сообщения"
        onClick={() => moodStore.set({ on: !mood.on })}
      >
        <Text size="lg" lh={1}>{mood.on ? '☀' : '☁'}</Text>
      </ActionIcon>
    </Tooltip>
  );
}

export function Encouragement() {
  const mood = useMood();
  const wh = useWarehouse();
  const [phrase, setPhrase] = useState<Phrase | null>(null);
  const prev = useRef<DayStats | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const stats = useApiQuery<DayStats>(['morale', wh], mood.on ? `/morale/today${qs({ warehouse_id: wh })}` : null,
    { refetchInterval: 90_000 });

  function show(pool: Phrase[]) {
    const s = moodStore.get();
    if (!mayShow(s, Date.now(), isBusy())) return;
    const picked = pickPhrase(pool, s.recent);
    if (!picked) return;
    moodStore.set({ count: s.count + 1, lastAt: Date.now(), recent: [...s.recent, picked.id] });
    setPhrase(picked);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setPhrase(null), SHOW_MS);
  }

  // работа сделана — счётчик смены вырос
  useEffect(() => {
    if (!stats.data) return;
    const pool = eventPhrases(prev.current, stats.data);
    prev.current = stats.data;
    if (pool.length) show(pool);
  }, [stats.data]);

  // давно ничего не происходило — редкая фоновая фраза
  useEffect(() => {
    if (!mood.on) return;
    const id = window.setInterval(() => {
      if (Date.now() - moodStore.get().lastAt >= IDLE_GAP_MS) show(idlePhrases());
    }, 5 * 60_000);
    return () => window.clearInterval(id);
  }, [mood.on]);

  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => { if (!mood.on) setPhrase(null); }, [mood.on]);

  return (
    <Transition mounted={!!phrase} transition="slide-up" duration={220}>
      {(style) => (
        <Paper
          style={{ ...style, position: 'fixed', right: 20, bottom: 20, zIndex: 300, maxWidth: 380 }}
          shadow="md" radius="md" p="sm" withBorder
        >
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <Text size="lg" lh={1.2}>☀</Text>
            <Text size="sm">{phrase?.text}</Text>
            <Group gap={4} wrap="nowrap">
              <Tooltip label="Больше не показывать">
                <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Больше не показывать"
                  onClick={() => moodStore.set({ on: false })}>⌀</ActionIcon>
              </Tooltip>
              <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Закрыть"
                onClick={() => setPhrase(null)}>×</ActionIcon>
            </Group>
          </Group>
        </Paper>
      )}
    </Transition>
  );
}
